import React, { useMemo, useState, useCallback } from "react";
import {
  Search,
  Sparkles,
  Target,
  TrendingUp,
  AlertTriangle,
  Skull,
  RotateCw,
  ArrowUpRight,
  Network,
  Zap,
  Brain,
  Compass,
  RefreshCw,
  Radio,
  Share2,
  LayoutGrid,
  Grid3x3,
  ExternalLink,
  Lock,
  GitBranch,
} from "lucide-react";
import WebGraph, { showsHookRate } from "./WebGraph";
import WebGridView from "./WebGridView";
import WebMatrixView from "./WebMatrixView";
import WebTreeView from "./WebTreeView";
import AdDetailModal, { BriefDraftModal, BrainScanModal } from "./AdDetailModal";
import WebFilterPanel, {
  computeMatches,
  DEFAULT_THRESHOLDS,
} from "./WebFilterPanel";
import { Sliders } from "lucide-react";
import { useCreativeActions } from "../lib/creativeActions";
import { briefLockReason } from "../lib/confidenceGating";
import WebChatPanel from "./WebChatPanel";
import { MessageSquare } from "lucide-react";
import {
  CONCEPTS,
  VARIATIONS,
  HOOKS,
  EDGES,
  ZOOM_LEVELS,
  FUNNEL_STAGES,
  ANGLES,
  STATUSES,
} from "../services/theWebData";
import { useMetaWebData } from "../hooks/useMetaWebData";
import "../styles/TheWeb.css";

const VIEWS = [
  { key: "web",    label: "Web",    icon: Share2 },
  { key: "grid",   label: "Grid",   icon: LayoutGrid },
  { key: "matrix", label: "Matrix", icon: Grid3x3 },
  { key: "tree",   label: "Tree",   icon: GitBranch },
];

/**
 * TheWebPage — the central concept-graph view.
 * Outline.md §5 + §8.1 + §10.3.
 *
 * Page anatomy (top → bottom):
 *   1. Header  — title + system pulse stats
 *   2. Daily Card Stack  — recommendation cards (§8.1)
 *   3. Toolbar — zoom level (L1-L4), funnel/angle filters, gap toggle, heat atlas, search
 *   4. Stage   — canvas graph + detail panel
 *   5. Legend  — overlay (status + edges)
 */
const TIME_RANGES = [
  { key: 7,  label: "7d" },
  { key: 14, label: "14d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
];
const DEFAULT_TOP_N = 50;

export default function TheWebPage({
  workspaceId = null,
  analysisModel = "",
  apiKeys = null,
  aiProfile = null,
  analysisKnowledge = null,
}) {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [funnelFilter, setFunnelFilter] = useState(new Set()); // empty = all
  const [angleFilter, setAngleFilter] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState(
    new Set(["active", "scaling", "paused", "unexplored"]),
  );
  const [showGaps, setShowGaps] = useState(true);
  const [showHeatAtlas, setShowHeatAtlas] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [view, setView] = useState("web");
  const [modalNodeId, setModalNodeId] = useState(null);
  const [days, setDays] = useState(30);
  const [enabledOnly, setEnabledOnly] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [filterMode, setFilterMode] = useState("highlight"); // 'highlight' | 'filter'
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // True when the user has narrowed any threshold from default — drives the
  // "engaged" badge on the toolbar trigger so it's obvious filters are live.
  const filterEngaged = useMemo(() => {
    return Object.entries(thresholds).some(
      ([, v]) => v.min > 0 || v.max !== Infinity,
    );
  }, [thresholds]);
  const activeThresholdCount = useMemo(() => {
    return Object.values(thresholds).filter(
      (v) => v.min > 0 || v.max !== Infinity,
    ).length;
  }, [thresholds]);

  // Selecting a node opens the modal; the side panel stays in sync too.
  const handleNodeSelect = useCallback((id) => {
    setSelectedId(id);
    if (id) setModalNodeId(id);
  }, []);

  const closeModal = useCallback(() => setModalNodeId(null), []);

  // Context passed to the brief generator (AIService config + active model).
  const briefContext = useMemo(
    () => ({ workspaceId, analysisModel, apiKeys, aiProfile, analysisKnowledge }),
    [workspaceId, analysisModel, apiKeys, aiProfile, analysisKnowledge],
  );

  // ---- Live Meta data (with seed fallback) -----------------------------

  const live = useMetaWebData({ days, workspaceId });
  const allNodes = live.nodes;
  const allEdges = live.edges;
  const dailyCards = live.dailyCards;

  // ---- Local lineage helpers (work against current data set) ----------

  const findNode = useCallback(
    (id) => allNodes.find((n) => n.id === id) || null,
    [allNodes],
  );

  const getChildren = useCallback(
    (id) =>
      allEdges
        .filter((e) => e.source === id && e.type === "parent")
        .map((e) => findNode(e.target))
        .filter(Boolean),
    [allEdges, findNode],
  );

  const getParent = useCallback(
    (id) => {
      const e = allEdges.find(
        (edge) => edge.target === id && edge.type === "parent",
      );
      return e ? findNode(e.source) : null;
    },
    [allEdges, findNode],
  );

  const getRelatedHooks = useCallback(
    (variationId) =>
      allEdges
        .filter(
          (e) => e.target === variationId && e.type === "cross_pollination",
        )
        .map((e) => findNode(e.source))
        .filter(Boolean),
    [allEdges, findNode],
  );

  // ---- Data filtering --------------------------------------------------

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();

    // Threshold matches — used for `filter` mode (hide non-match) or
    // `highlight` mode (dim non-match). Computed against the unfiltered
    // node set so the panel summary count is honest.
    const thresholdMatches = computeMatches(allNodes, thresholds);

    // Pre-compute the set of creatives that are still ACTIVE per Meta.
    // Used both to filter creatives directly and to keep hooks only if at
    // least one grafted creative is still running.
    const activeCreativeIds = new Set();
    if (enabledOnly) {
      allNodes.forEach((n) => {
        if (n.type === "concept" || n.type === "variation") {
          const es = String(n._meta?.effective_status || "").toUpperCase();
          // No _meta means seed data (no Meta status known) — keep it.
          if (!n._meta) {
            activeCreativeIds.add(n.id);
          } else if (es === "ACTIVE" || !n._meta.effective_status) {
            activeCreativeIds.add(n.id);
          }
        }
      });
    }

    let matches = allNodes.filter((n) => {
      if ((n.level || 1) > zoomLevel) return false;
      if (funnelFilter.size && n.funnel && !funnelFilter.has(n.funnel)) return false;
      if (angleFilter.size && n.angle && !angleFilter.has(n.angle)) return false;
      // The grid has its own explicit status filter (Active/Paused/Killed/All)
      // — don't pre-filter statuses away from it here.
      if (view !== "grid" && statusFilter.size && n.status && !statusFilter.has(n.status)) return false;
      if (!showGaps && n.status === "unexplored") return false;
      // Enabled-only: reject anything Meta marked PAUSED / DISAPPROVED / etc.
      // Hooks pass only if at least one grafted creative is still active.
      if (enabledOnly) {
        if (n.type === "concept" || n.type === "variation") {
          if (!activeCreativeIds.has(n.id)) return false;
        } else if (n.type === "hook") {
          const grafted = allEdges.filter(
            (e) => e.target === n.id || e.source === n.id,
          );
          const hasActiveLink = grafted.some((e) => {
            const otherId = e.source === n.id ? e.target : e.source;
            return activeCreativeIds.has(otherId);
          });
          // If a hook has no edges at all (orphan in seed data) keep it; only
          // drop hooks whose every grafted creative is paused/killed.
          if (grafted.length > 0 && !hasActiveLink) return false;
        }
      }
      // Threshold filter (only in 'filter' mode — highlight mode keeps all)
      if (filterMode === "filter" && filterEngaged && !thresholdMatches.has(n.id)) {
        return false;
      }
      if (term) {
        const blob = `${n.label || ""} ${n.angle || ""} ${n.archetype || ""}`.toLowerCase();
        if (!blob.includes(term)) return false;
      }
      return true;
    });

    // Default top-N spend cap so the graph isn't a hairball with 300+ nodes.
    // Hooks always count toward the visible set (they're cheap) — only
    // concept/variation nodes get capped.
    const totalEligible = matches.length;
    let capped = false;
    if (!showAll && view === "web") {
      const concepts = matches
        .filter((n) => n.type === "concept" || n.type === "variation")
        .sort((a, b) => (b.spend || 0) - (a.spend || 0));
      if (concepts.length > DEFAULT_TOP_N) {
        capped = true;
        const keep = new Set(concepts.slice(0, DEFAULT_TOP_N).map((n) => n.id));
        matches = matches.filter(
          (n) => n.type === "hook" || keep.has(n.id),
        );
      }
    }

    // Always keep at least the parent of any visible variation, so trees stay connected
    const ids = new Set(matches.map((n) => n.id));
    const all2 = matches.slice();
    matches.forEach((n) => {
      if (n.parent && !ids.has(n.parent)) {
        const p = allNodes.find((x) => x.id === n.parent);
        if (p && (p.level || 1) <= zoomLevel) {
          all2.push(p);
          ids.add(p.id);
        }
      }
    });

    const visEdges = allEdges.filter(
      (e) => ids.has(e.source) && ids.has(e.target),
    );

    // Compute dimmedIds for highlight mode — nodes that pass primary filters
    // but don't match the threshold criteria.
    const dimmedIds = new Set();
    if (filterMode === "highlight" && filterEngaged) {
      all2.forEach((n) => {
        if (n.type === "hook" || n.status === "unexplored") return;
        if (!thresholdMatches.has(n.id)) dimmedIds.add(n.id);
      });
    }

    return { nodes: all2, edges: visEdges, capped, totalEligible, dimmedIds };
  }, [zoomLevel, funnelFilter, angleFilter, statusFilter, showGaps, search, allNodes, allEdges, enabledOnly, showAll, view, thresholds, filterMode, filterEngaged]);

  // ---- Selectors -------------------------------------------------------

  const selectedNode = useMemo(() => findNode(selectedId), [findNode, selectedId]);

  const stats = useMemo(() => {
    const total = allNodes.length;
    const active = allNodes.filter((n) => n.status === "active" || n.status === "scaling").length;
    const scaling = allNodes.filter((n) => n.scaling || n.status === "scaling").length;
    const winners = allNodes.filter((n) => (n.roas || 0) >= 3 && n.status !== "unexplored").length;
    const gaps = allNodes.filter((n) => n.status === "unexplored").length;
    const untagged = allNodes.filter(
      (n) => (n.type === "concept" || n.type === "variation") && !n.angle && !n.funnel,
    ).length;
    // "Gaps" only makes sense once the AI tagging + clustering pass has run.
    // Until then, surface the untagged count instead so the stat doesn't
    // read as a broken zero.
    const tagsRunYet = allNodes.some((n) => n.angle || n.funnel || n.awareness);
    return { total, active, scaling, winners, gaps, untagged, tagsRunYet };
  }, [allNodes]);

  // ---- Handlers --------------------------------------------------------

  const handleCardClick = (card) => {
    setSelectedId(card.nodeId);
    setHighlightId(card.nodeId);
    // bring the node into the visible set if it was filtered out
    setStatusFilter((prev) => {
      const next = new Set(prev);
      next.add("active"); next.add("scaling"); next.add("paused");
      next.add("killed"); next.add("unexplored");
      return next;
    });
    setTimeout(() => setHighlightId(null), 4000);
  };

  const toggleSetItem = (set, key, setter) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setter(next);
  };

  return (
    <div className="web-root">
      {/* HEADER */}
      <div className="web-header">
        <div>
          <h1 className="web-header__title">
            <span className="web-header__title-mark" />
            The Web
            <DataSourceBadge live={live} />
          </h1>
          <p className="web-header__sub">
            Every angle you've tested, every variation, every hook — connected. Saturated nodes are spending right now.
            Greyed nodes are unexplored territory waiting for a brief.
          </p>
        </div>
        <div className="web-header__stats">
          <Stat label="Active" value={stats.active} />
          <Stat label="Scaling" value={stats.scaling} gold />
          <Stat label="Winners" value={stats.winners} />
          {stats.tagsRunYet ? (
            <Stat label="Gaps" value={stats.gaps} />
          ) : (
            <Stat
              label="Untagged"
              value={stats.untagged}
              hint="Run AI tagging (Phase 2) to enable gap analysis"
            />
          )}
        </div>
      </div>

      {/* DAILY CARDS (§8.1) */}
      <div className="web-cards">
        <div className="web-cards__head">
          <span className="web-cards__title">
            Today's stack · <b>{dailyCards.length} {dailyCards.length === 1 ? "decision" : "decisions"} waiting</b>
          </span>
          <span style={{ fontFamily: "var(--w-mono)", fontSize: 11, color: "var(--w-muted-2)" }}>
            {live.range ? `${live.range.since} → ${live.range.until}` : "seed data"}
          </span>
        </div>
        <div className="web-cards__row">
          {dailyCards.map((c) => (
            <DailyCard key={c.id} card={c} onClick={() => handleCardClick(c)} />
          ))}
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="web-toolbar">
        <div className="web-view-switch" role="tablist" aria-label="Visualization">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            return (
              <button
                key={v.key}
                className="web-view-switch__opt"
                data-active={view === v.key}
                onClick={() => setView(v.key)}
              >
                <Icon size={13} />
                {v.label}
              </button>
            );
          })}
        </div>

        <span className="web-toolbar__divider" />

        <div className="web-zoom" role="tablist" aria-label="Time range">
          {TIME_RANGES.map((t) => (
            <button
              key={t.key}
              className="web-zoom__opt"
              data-active={days === t.key}
              onClick={() => setDays(t.key)}
              title={`Last ${t.label}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {view === "web" ? (
          <>
            <span className="web-toolbar__divider" />

            <div className="web-zoom" role="tablist" aria-label="Zoom level">
              {ZOOM_LEVELS.map((z) => (
                <button
                  key={z.key}
                  className="web-zoom__opt"
                  data-active={zoomLevel === z.key}
                  onClick={() => setZoomLevel(z.key)}
                  title={z.sub}
                >
                  {z.label}
                </button>
              ))}
            </div>
          </>
        ) : null}

        <span className="web-toolbar__divider" />

        <div className="web-filters">
          {FUNNEL_STAGES.map((f) => (
            <button
              key={f.key}
              className="web-chip"
              data-active={funnelFilter.has(f.key)}
              onClick={() => toggleSetItem(funnelFilter, f.key, setFunnelFilter)}
            >
              <span className="web-chip__dot" style={{ background: f.color }} />
              {f.label}
            </button>
          ))}
        </div>

        <span className="web-toolbar__divider" />

        <div className="web-filters">
          {ANGLES.slice(0, 6).map((a) => (
            <button
              key={a.key}
              className="web-chip"
              data-active={angleFilter.has(a.key)}
              onClick={() => toggleSetItem(angleFilter, a.key, setAngleFilter)}
            >
              {a.label}
            </button>
          ))}
        </div>

        <span className="web-toolbar__divider" />

        <label className="web-toggle">
          <input
            type="checkbox"
            checked={enabledOnly}
            onChange={(e) => setEnabledOnly(e.target.checked)}
          />
          <span className="web-toggle__switch" />
          Enabled only
        </label>

        <button
          type="button"
          className="web-filter-trigger"
          data-active={filterPanelOpen}
          data-engaged={filterEngaged}
          onClick={() => setFilterPanelOpen((v) => !v)}
          title="Granular threshold filter (CTR, ROAS, spend, hook rate)"
        >
          <Sliders size={12} />
          Thresholds
          {activeThresholdCount > 0 ? (
            <span className="web-filter-trigger__badge">{activeThresholdCount}</span>
          ) : null}
        </button>

        {view === "web" ? (
          <>
            <label className="web-toggle">
              <input
                type="checkbox"
                checked={showGaps}
                onChange={(e) => setShowGaps(e.target.checked)}
              />
              <span className="web-toggle__switch" />
              Show gaps
            </label>

            <label className="web-toggle">
              <input
                type="checkbox"
                checked={showHeatAtlas}
                onChange={(e) => setShowHeatAtlas(e.target.checked)}
              />
              <span className="web-toggle__switch" />
              Heat Atlas
            </label>
          </>
        ) : null}

        <button
          type="button"
          className={`web-pill${chatOpen ? " web-pill--active" : ""}`}
          onClick={() => setChatOpen((v) => !v)}
          title="Chat with the AI about this view"
        >
          <MessageSquare size={13} /> Chat
        </button>

        <div className="web-search">
          <Search size={13} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search concepts, hooks, angles…"
          />
        </div>
      </div>

      {/* STAGE */}
      {view === "web" ? (
        <div className="web-stage">
          <div className="web-canvas-wrap">
            {visible.capped ? (
              <div
                className="web-cap-banner"
                title={`Rendering more than ${DEFAULT_TOP_N} nodes makes the force-directed layout sluggish and labels overlap. The cap surfaces the top spenders first; click "Show all" to render every match (may briefly slow the graph).`}
              >
                Showing top {DEFAULT_TOP_N} by spend of {visible.totalEligible}.
                The graph caps node count for readability — concept clustering will collapse near-duplicates in Phase 2.
                <button className="web-cap-banner__btn" onClick={() => setShowAll(true)}>
                  Show all
                </button>
              </div>
            ) : null}
            {showAll && view === "web" ? (
              <div className="web-cap-banner">
                Showing all {visible.nodes.length} nodes.
                <button className="web-cap-banner__btn" onClick={() => setShowAll(false)}>
                  Cap to top {DEFAULT_TOP_N}
                </button>
              </div>
            ) : null}
            <WebGraph
              nodes={visible.nodes}
              edges={visible.edges}
              highlightId={highlightId}
              selectedId={selectedId}
              onSelect={handleNodeSelect}
              showHeatAtlas={showHeatAtlas}
              showGaps={showGaps}
              dimmedIds={visible.dimmedIds}
            />

            <WebFilterPanel
              open={filterPanelOpen}
              onClose={() => setFilterPanelOpen(false)}
              thresholds={thresholds}
              setThresholds={setThresholds}
              mode={filterMode}
              setMode={setFilterMode}
              nodes={allNodes}
            />

            <div className="web-legend">
              <div className="web-legend__title">Legend</div>
              <div className="web-legend__row">
                <span className="web-legend__sw web-legend__sw--scaling" /> Scaling
              </div>
              <div className="web-legend__row">
                <span className="web-legend__sw" /> Active
              </div>
              <div className="web-legend__row">
                <span className="web-legend__sw web-legend__sw--paused" /> Paused
              </div>
              <div className="web-legend__row">
                <span className="web-legend__sw web-legend__sw--killed" /> Killed
              </div>
              <div className="web-legend__row">
                <span className="web-legend__sw web-legend__sw--gap" /> Unexplored
              </div>
              <div className="web-legend__row">
                <span className="web-legend__sw web-legend__sw--gold" /> ROAS &ge; 4
              </div>
              <div className="web-divider" />
              <div className="web-legend__row">
                <span className="web-legend__edge" /> Parent → child
              </div>
              <div className="web-legend__row">
                <span className="web-legend__edge web-legend__edge--mut" /> Mutation
              </div>
              <div className="web-legend__row">
                <span className="web-legend__edge web-legend__edge--cross" /> Hook reuse
              </div>
            </div>
          </div>

          <DetailPanel
            node={selectedNode}
            onSelect={handleNodeSelect}
            getChildren={getChildren}
            getParent={getParent}
            getRelatedHooks={getRelatedHooks}
            allEdges={allEdges}
            allNodes={allNodes}
            context={briefContext}
          />
        </div>
      ) : view === "grid" ? (
        <WebGridView
          nodes={visible.nodes}
          selectedId={selectedId}
          onSelect={handleNodeSelect}
          dimmedIds={visible.dimmedIds}
        />
      ) : view === "matrix" ? (
        <WebMatrixView
          nodes={visible.nodes}
          onSelect={handleNodeSelect}
        />
      ) : view === "tree" ? (
        <WebTreeView
          nodes={visible.nodes}
          edges={visible.edges}
          selectedId={selectedId}
          onSelect={handleNodeSelect}
        />
      ) : null}

      <AdDetailModal
        node={modalNodeId ? findNode(modalNodeId) : null}
        onClose={closeModal}
        onSelect={(id) => {
          setSelectedId(id);
          setModalNodeId(id);
        }}
        getChildren={getChildren}
        getParent={getParent}
        getRelatedHooks={getRelatedHooks}
        allNodes={allNodes}
        allEdges={allEdges}
        context={briefContext}
      />

      {chatOpen ? (
        <div className="web-chat-shell">
          <WebChatPanel
            open={chatOpen}
            onClose={() => setChatOpen(false)}
            workspaceId={workspaceId}
            selectedNode={selectedId ? findNode(selectedId) : null}
            nodes={allNodes}
            edges={allEdges}
            summary={live.summary}
            range={live.range}
            analysisModel={analysisModel}
            apiKeys={apiKeys}
            aiProfile={aiProfile}
            analysisKnowledge={analysisKnowledge}
          />
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  DataSourceBadge — shows live / fallback / loading                   */
/* -------------------------------------------------------------------- */

const DataSourceBadge = ({ live }) => {
  const { status, source, reload } = live;
  const cfg = {
    live:     { dot: "#4ade80", text: "live · meta" },
    loading:  { dot: "#22d3ee", text: "loading" },
    fallback: { dot: "#a1a1aa", text: "seed data" },
    error:    { dot: "#ec4899", text: "error" },
    idle:     { dot: "#a1a1aa", text: "idle" },
  }[status] || { dot: "#a1a1aa", text: source };

  return (
    <span
      onClick={reload}
      title={
        status === "fallback"
          ? "No live Meta connection — showing seed data. Click to retry."
          : status === "error"
            ? `Error: ${live.errorMessage || "unknown"}. Click to retry.`
            : "Click to refresh"
      }
      style={{
        marginLeft: 12,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontFamily: "var(--w-mono)",
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        padding: "3px 9px",
        borderRadius: 999,
        border: "1px solid var(--w-border)",
        background: "var(--w-surface)",
        color: "var(--w-muted)",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: cfg.dot,
          boxShadow: `0 0 8px ${cfg.dot}`,
        }}
      />
      {cfg.text}
      {status === "loading" ? (
        <RefreshCw size={10} style={{ animation: "spin 1s linear infinite" }} />
      ) : null}
    </span>
  );
};

/* -------------------------------------------------------------------- */
/*  Subcomponents                                                       */
/* -------------------------------------------------------------------- */

const Stat = ({ label, value, gold, hint }) => (
  <div
    className={`web-stat ${gold ? "web-stat--gold" : ""}`}
    title={hint || undefined}
  >
    <div className="web-stat__label">{label}</div>
    <div className="web-stat__value">{value}</div>
  </div>
);

const DailyCard = ({ card, onClick }) => {
  const Icon = {
    scale:    TrendingUp,
    fatigue:  AlertTriangle,
    kill:     Skull,
    gap:      Sparkles,
    transfer: ArrowUpRight,
    revive:   RotateCw,
  }[card.kind] || Sparkles;

  return (
    <article className="web-card" data-kind={card.kind} onClick={onClick}>
      <div className="web-card__head">
        <span className="web-card__icon"><Icon size={13} /></span>
        <span className="web-card__title">{card.title}</span>
      </div>
      <p className="web-card__body">{card.body}</p>
      <div className="web-card__actions">
        {card.actions.map((a) => (
          <button
            key={a}
            className={`web-btn ${a === "Scale" || a === "Brief" ? "web-btn--gold" : "web-btn--ghost"}`}
            style={{ fontSize: 11, padding: "5px 10px" }}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            {a}
          </button>
        ))}
      </div>
    </article>
  );
};

const DetailPanel = ({ node, onSelect, getChildren, getParent, getRelatedHooks, allEdges, allNodes, context }) => {
  if (!node) {
    return (
      <aside className="web-detail">
        <div className="web-detail__empty">
          <div>
            <div className="web-detail__empty-mark">
              <Network size={22} />
            </div>
            <div className="web-detail__empty-title">Click a node</div>
            <div className="web-detail__empty-sub">
              Concepts, variations, and hooks each have their own detail breakdown — performance, lineage, AI thesis, and one-click brief generation.
            </div>
          </div>
        </div>
      </aside>
    );
  }

  if (node.type === "hook") {
    return (
      <HookDetail
        node={node}
        onSelect={onSelect}
        allEdges={allEdges}
        allNodes={allNodes}
      />
    );
  }
  return (
    <CreativeDetail
      node={node}
      onSelect={onSelect}
      getChildren={getChildren}
      getParent={getParent}
      getRelatedHooks={getRelatedHooks}
      context={context}
    />
  );
};

const CreativeDetail = ({ node, onSelect, getChildren, getParent, getRelatedHooks, context }) => {
  const isUnexplored = node.status === "unexplored";
  const children = getChildren(node.id);
  const parent = getParent(node.id);
  const hooks = getRelatedHooks(node.id);
  const stage = node.funnel ? node.funnel : null;
  const meta = node._meta || {};
  const {
    briefOpen,
    closeBrief,
    brainOpen,
    closeBrain,
    briefLocked,
    handleBrief,
    handleSendToBrain,
    handleReferenceWinners,
    handleAddToTestQueue,
  } = useCreativeActions(node);
  const rawName = meta.ad_name || meta.creative_name || "";
  const showRawName =
    rawName && rawName.trim().toLowerCase() !== String(node.label || "").trim().toLowerCase();
  const previewHref = meta.ad_id
    ? `https://www.facebook.com/ads/library/?id=${encodeURIComponent(meta.ad_id)}`
    : node.thumbnail || "";

  return (
    <aside className="web-detail">
      <div className="web-detail__head">
        {node.thumbnail ? (
          <img
            src={node.thumbnail}
            alt={node.label || "Creative thumbnail"}
            className="web-detail__thumb"
            loading="lazy"
          />
        ) : null}
        <div className="web-detail__kind">
          {node.type === "concept" ? "CONCEPT" : "VARIATION"}
          {node.scaling ? " · SCALING" : ""}
          {isUnexplored ? " · UNEXPLORED" : ""}
        </div>
        <h2 className="web-detail__title">{node.label}</h2>
        {showRawName ? (
          <div className="web-detail__subtitle" title={rawName}>
            {rawName}
          </div>
        ) : null}
        {previewHref ? (
          <a
            className="web-detail__preview"
            href={previewHref}
            target="_blank"
            rel="noreferrer noopener"
          >
            <ExternalLink size={12} /> Preview
          </a>
        ) : null}
        <div className="web-detail__chips">
          {stage ? (
            <span className={`web-detail__chip web-detail__chip--${stage}`}>{stage.toUpperCase()}</span>
          ) : null}
          {node.awareness ? (
            <span className="web-detail__chip">{node.awareness}-aware</span>
          ) : null}
          {node.angle ? (
            <span className="web-detail__chip">{node.angle}</span>
          ) : null}
          {node.format ? (
            <span className="web-detail__chip">{node.format.replace(/_/g, " ")}</span>
          ) : null}
          {node.scaling ? (
            <span className="web-detail__chip web-detail__chip--scale">scaling</span>
          ) : null}
        </div>
      </div>

      <div className="web-detail__body">
        {!isUnexplored && (
          <div className="web-detail__section">
            <div className="web-detail__label">Performance</div>
            <div className="web-stats-grid">
              <div className="web-stats-grid__cell">
                <div className="web-stats-grid__k">Spend</div>
                <div className="web-stats-grid__v">${(node.spend || 0).toLocaleString()}</div>
              </div>
              <div className="web-stats-grid__cell">
                <div className="web-stats-grid__k">ROAS</div>
                <div className={`web-stats-grid__v ${(node.roas || 0) >= 4 ? "web-stats-grid__v--gold" : (node.roas || 0) >= 3 ? "web-stats-grid__v--good" : (node.roas || 0) < 2 ? "web-stats-grid__v--bad" : ""}`}>
                  {(node.roas || 0).toFixed(1)}
                </div>
              </div>
              <div className="web-stats-grid__cell">
                <div className="web-stats-grid__k">CTR</div>
                <div className="web-stats-grid__v">{(node.ctr || 0).toFixed(1)}%</div>
              </div>
              {showsHookRate(node) ? (
                <div className="web-stats-grid__cell">
                  <div className="web-stats-grid__k">Hook rate</div>
                  <div className="web-stats-grid__v">{Math.round((node.hookRate || 0) * 100)}%</div>
                </div>
              ) : null}
              <div className="web-stats-grid__cell">
                <div className="web-stats-grid__k">Confidence</div>
                <div className="web-stats-grid__v">{Math.round((node.confidence || 0) * 100)}%</div>
              </div>
              <div className="web-stats-grid__cell">
                <div className="web-stats-grid__k">Status</div>
                <div className="web-stats-grid__v" style={{ fontSize: 14 }}>
                  {(STATUSES[node.status] || STATUSES.active).label}
                </div>
              </div>
            </div>
          </div>
        )}

        {node.thesis && (
          <div className="web-detail__section">
            <div className="web-detail__label">
              {isUnexplored ? "Why this is the next test" : "Why it's working"}
            </div>
            <div className="web-thesis">{node.thesis}</div>
          </div>
        )}

        {!isUnexplored && (
          <div className="web-detail__section">
            <div className="web-detail__label">AI thesis · element responsibility</div>
            <AiThesis node={node} />
          </div>
        )}

        {parent && (
          <div className="web-detail__section">
            <div className="web-detail__label">Parent concept</div>
            <div className="web-rel">
              <div className="web-rel__item" onClick={() => onSelect(parent.id)}>
                <span className="web-rel__type">PARENT</span>
                <span className="web-rel__name">{parent.label}</span>
                <span className="web-rel__metric">${((parent.spend || 0) / 1000).toFixed(0)}k</span>
              </div>
            </div>
          </div>
        )}

        {children.length > 0 && (
          <div className="web-detail__section">
            <div className="web-detail__label">{node.type === "concept" ? "Variations" : "Mutations"} · {children.length}</div>
            <div className="web-rel">
              {children.map((c) => (
                <div key={c.id} className="web-rel__item" onClick={() => onSelect(c.id)}>
                  <span className="web-rel__type">{(c.status || "active").slice(0, 6).toUpperCase()}</span>
                  <span className="web-rel__name">{c.label}</span>
                  <span className="web-rel__metric">{(c.roas || 0).toFixed(1)}x</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {hooks.length > 0 && (
          <div className="web-detail__section">
            <div className="web-detail__label">Grafted hooks</div>
            <div className="web-rel">
              {hooks.map((h) => (
                <div key={h.id} className="web-rel__item" onClick={() => onSelect(h.id)}>
                  <span className="web-rel__type">HOOK</span>
                  <span className="web-rel__name">{h.label}</span>
                  <span className="web-rel__metric">{Math.round((h.winRate || 0) * 100)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="web-detail__section">
          <div className="web-detail__label">Actions</div>
          <div className="web-actions">
            <button
              type="button"
              className="web-btn web-btn--gold web-btn--full"
              onClick={handleBrief}
              disabled={briefLocked}
              title={
                briefLocked
                  ? briefLockReason(node)
                  : "Generate a variation brief from this creative"
              }
            >
              {briefLocked ? <Lock size={13} /> : <Sparkles size={13} />}
              {briefLocked ? "Brief locked · low confidence" : "Generate variation brief"}
            </button>
            {!isUnexplored ? (
              <>
                <button
                  type="button"
                  className="web-btn web-btn--full"
                  onClick={handleSendToBrain}
                >
                  <Brain size={13} /> Send to Brain
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="web-btn web-btn--full"
                  onClick={handleReferenceWinners}
                >
                  <Compass size={13} /> Reference winners
                </button>
                <button
                  type="button"
                  className="web-btn web-btn--full"
                  onClick={handleAddToTestQueue}
                >
                  <Target size={13} /> Add to test queue
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {briefOpen ? (
        <BriefDraftModal
          node={node}
          parent={parent}
          onClose={closeBrief}
          context={context}
        />
      ) : null}

      {brainOpen ? (
        <BrainScanModal node={node} onClose={closeBrain} context={context} />
      ) : null}
    </aside>
  );
};

const HookDetail = ({ node, onSelect, allEdges = EDGES, allNodes = [...CONCEPTS, ...VARIATIONS] }) => {
  const grafted = allEdges
    .filter((e) => e.source === node.id && e.type === "cross_pollination")
    .map((e) => allNodes.find((n) => n.id === e.target))
    .filter(Boolean);

  return (
    <aside className="web-detail">
      <div className="web-detail__head">
        <div className="web-detail__kind">HOOK · REUSABLE</div>
        <h2 className="web-detail__title">{node.label}</h2>
        <div className="web-detail__chips">
          <span className="web-detail__chip">{node.archetype}</span>
          <span className="web-detail__chip">reuses · {node.reuses}</span>
        </div>
      </div>

      <div className="web-detail__body">
        <div className="web-detail__section">
          <div className="web-detail__label">Hook performance</div>
          <div className="web-stats-grid">
            <div className="web-stats-grid__cell">
              <div className="web-stats-grid__k">Win rate</div>
              <div className="web-stats-grid__v web-stats-grid__v--gold">
                {Math.round((node.winRate || 0) * 100)}%
              </div>
            </div>
            <div className="web-stats-grid__cell">
              <div className="web-stats-grid__k">Reuses</div>
              <div className="web-stats-grid__v">{node.reuses}</div>
            </div>
          </div>
        </div>

        {grafted.length > 0 && (
          <div className="web-detail__section">
            <div className="web-detail__label">Grafted onto · {grafted.length}</div>
            <div className="web-rel">
              {grafted.map((g) => (
                <div key={g.id} className="web-rel__item" onClick={() => onSelect(g.id)}>
                  <span className="web-rel__type">{(g.type || "var").slice(0, 4).toUpperCase()}</span>
                  <span className="web-rel__name">{g.label}</span>
                  <span className="web-rel__metric">{(g.roas || 0).toFixed(1)}x</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="web-detail__section">
          <div className="web-detail__label">Actions</div>
          <div className="web-actions">
            <button className="web-btn web-btn--gold web-btn--full">
              <Zap size={13} /> Graft onto fatigued concept
            </button>
            <button className="web-btn web-btn--full">
              <Sparkles size={13} /> Generate 5 visual variations
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

/* -------------------------------------------------------------------- */
/*  AiThesis — KB-cited thesis when available, hand-coded fallback else  */
/* -------------------------------------------------------------------- */

const AiThesis = ({ node }) => {
  const a = node?.analysis || {};
  const idea = a.idea || a.thesis_idea || a.thesis?.idea || dnaThesis(node, "idea");
  const mechanism =
    a.mechanism || a.thesis_mechanism || a.thesis?.mechanism || dnaThesis(node, "mechanism");
  const lever = a.lever || a.thesis_lever || a.thesis?.lever || dnaThesis(node, "lever");
  const citations = Array.isArray(a.citations)
    ? a.citations
    : Array.isArray(node?.citations)
      ? node.citations
      : [];
  const hasReal = Boolean(a.idea || a.thesis || a.thesis_idea);
  return (
    <div className="web-thesis" style={{ fontStyle: "normal" }}>
      <strong style={{ color: "var(--w-fg)" }}>The single idea:</strong>{" "}
      {idea}
      <br />
      <strong style={{ color: "var(--w-fg)" }}>What made it land:</strong>{" "}
      {mechanism}
      <br />
      <strong style={{ color: "var(--w-fg)" }}>Most likely cause:</strong>{" "}
      {lever}
      {citations.length ? (
        <div className="web-thesis__cites">
          {citations.map((c, i) => (
            <span key={i} className="web-thesis__cite" title={c.claim || ""}>
              {c.source}
              {c.locator ? `, ${c.locator}` : ""}
            </span>
          ))}
        </div>
      ) : !hasReal ? (
        <div className="web-thesis__placeholder">
          Hand-coded fallback — KB-cited thesis appears here once analysis runs with a populated knowledge base.
        </div>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

function dnaThesis(n, slot) {
  // Hand-crafted lines for the seed set — in production this is the
  // outline §7.4 "Why It Won" generator output, RAG-cited.
  const m = {
    c_wife_notices: {
      idea: "Grooming as relational care, not vanity.",
      mechanism: "Wife-POV opens a private outcome the buyer wants but won't admit.",
      lever: "Hook (text-screenshot specificity).",
    },
    v_wn_v2c: {
      idea: "Make the headline carry the proof.",
      mechanism: "Specificity ('14 days') closes the credibility gap on a fear product.",
      lever: "Headline craft — visual is unchanged from v2.",
    },
    c_founder_letter: {
      idea: "Authority + mechanism reveal.",
      mechanism: "Founder explains why drugstore tools fail skin below the belt.",
      lever: "Trust transfer (face + receipts).",
    },
    c_specificity_14d: {
      idea: "Concrete window beats vague promise.",
      mechanism: "'14 days' is verifiable — buyer can check themselves.",
      lever: "Headline + sticky-note format.",
    },
  };
  return m[n.id]?.[slot] || {
    idea: `${n.angle || "Angle"} · ${n.awareness || "problem"}-aware framing.`,
    mechanism: "Pattern interrupt + specific outcome promise in first 3s.",
    lever: "Hook archetype is doing the heavy lifting.",
  }[slot];
}
